import { AxiosError } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import React from 'react'
import useSWR from 'swr'
import { RolodexFileRef } from 'types'

interface RolodexFileMetadataQuery {
  entry_id: string
  path: string
}

export const useRolodexFileMetadata = (
  query: RolodexFileMetadataQuery | undefined
): {
  /** The file metadata returned from rolodex */
  metadata: RolodexFileRef | undefined
  /** Whether the SWR query is still loading */
  isLoading: boolean
  /** Whether the SWR query is still validating */
  isValidating: boolean
  /** To account for the initial case before the query fires */
  hasEverLoaded: boolean
  /** Any errors returned from SWR */
  error: AxiosError
  /** A callback for forcing a re-fetch */
  forceRefresh: () => Promise<void>
} => {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)

  const fetcher = React.useCallback(() => {
    if (!query || !isAuthenticated) {
      return Promise.resolve(undefined)
    }
    return axios
      .get(`${apiURL}/api/v2/files/metadata/${query.entry_id}/${query.path}`)
      .then((res) => res.data)
  }, [apiURL, axios, isAuthenticated, query])

  const { data, isLoading, isValidating, error, mutate } = useSWR(
    query && isAuthenticated
      ? JSON.stringify({ type: 'file_metadata', ...query })
      : null,
    fetcher
  )

  const forceRefresh = React.useCallback(
    async () => await mutate().then(() => {}),
    [mutate]
  )

  return {
    metadata: data ?? undefined,
    isLoading,
    isValidating,
    error,
    hasEverLoaded: !!data,
    forceRefresh,
  }
}
